/**
 * AddToCollectionMenu — small dropdown that lets a signed-in user add/remove an
 * item from one or more of their collections (shelves). Polymorphic: works for
 * both concept pins and saved Oracle correlations.
 *
 * Props:
 *   - kind: "concept" | "correlation"
 *   - itemId: concept_id (for "concept") or oracle_save.id (for "correlation")
 *
 * Concept calls /pins/{id}/shelves and auto-creates a private pin if none exists.
 * Correlation calls /saves/{id}/shelves and updates shelf_ids on the oracle_save.
 */
import React, { useEffect, useRef, useState } from "react";
import { Plus, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function AddToCollectionMenu({ kind = "concept", itemId, conceptId, label }) {
  // `conceptId` retained for backwards compatibility with existing callers.
  const effectiveId = itemId || conceptId;
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [shelves, setShelves] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  // Anchor the popup to whichever side of the trigger has more room — flipping
  // prevents the menu from clipping off-screen when the trigger sits near a viewport edge.
  const [anchor, setAnchor] = useState("right");
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Decide anchor side based on the trigger's position. Popup is 18rem (288px) wide;
    // if more space on the right of the trigger, anchor LEFT (popup extends right); else anchor RIGHT.
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const POPUP_W = 288;
      // Prefer left-anchor when there is room to the right of the trigger's LEFT edge.
      setAnchor(rect.left + POPUP_W <= vw - 8 ? "left" : "right");
    }
    let cancelled = false;
    (async () => {
      try {
        const shelvesRes = await api.get("/shelves/me");
        if (cancelled) return;
        setShelves(shelvesRes.data || []);

        if (kind === "concept") {
          // Concept pins don't expose shelf_ids on the per-concept endpoint —
          // we look ourselves up in /pins/me.
          const myPinsRes = await api.get("/pins/me");
          const me = (myPinsRes.data || []).find((p) => p.concept_id === effectiveId);
          setSelected(new Set(me?.shelf_ids || []));
        } else {
          const res = await api.get(`/saves/${effectiveId}/shelves`);
          setSelected(new Set(res.data?.shelf_ids || []));
        }
      } catch { /* ignore */ }
    })();
    const onDocClick = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => { cancelled = true; document.removeEventListener("mousedown", onDocClick); };
  }, [open, kind, effectiveId]);

  if (!user) return null;

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      // If the user typed a new-collection name but didn't tap the `+` button,
      // treat Save as "create + attach". Otherwise the typed text would be
      // silently discarded — a frequent UX trap when there are two side-by-side
      // confirmation affordances.
      let shelfIds = Array.from(selected);
      const pendingName = newName.trim();
      if (pendingName) {
        const { data: created } = await api.post("/shelves", { name: pendingName });
        setShelves((arr) => [...arr, { ...created, item_count: 0, pin_count: 0 }]);
        setNewName("");
        shelfIds = [...shelfIds, created.id];
        setSelected(new Set(shelfIds));
      }
      const url = kind === "concept"
        ? `/pins/${effectiveId}/shelves`
        : `/saves/${effectiveId}/shelves`;
      await api.put(url, { shelf_ids: shelfIds });
      toast.success(pendingName ? `Created "${pendingName}" and added.` : "Saved.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };

  const createAndAdd = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data: shelf } = await api.post("/shelves", { name });
      setShelves((arr) => [...arr, { ...shelf, item_count: 0, pin_count: 0 }]);
      setSelected((s) => new Set([...s, shelf.id]));
      setNewName("");
      toast.success(`Created "${shelf.name}".`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create collection.");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        ref={triggerRef}
        data-testid="add-to-collection-trigger"
        onClick={() => setOpen((v) => !v)}
        className="reaction-pill"
        title="Add to a collection"
      >
        <Plus size={12} strokeWidth={2} /> {label || "Add to collection"}
      </button>
      {open && (
        <div
          data-testid="add-to-collection-menu"
          className={`absolute top-full mt-2 z-30 w-72 max-w-[calc(100vw-1.5rem)] bg-background hairline shadow-2xl p-3 ${
            anchor === "left" ? "left-0" : "right-0"
          }`}
        >
          <div className="label mb-2">Your collections</div>
          {shelves.length === 0 ? (
            <p className="serif italic text-sm text-muted-foreground py-2">
              No collections yet — create one below.
            </p>
          ) : (
            <ul className="max-h-48 overflow-y-auto -mx-1 mb-2">
              {shelves.map((s) => {
                const on = selected.has(s.id);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      data-testid={`shelf-pick-${s.id}`}
                      onClick={() => toggle(s.id)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-2 hover:bg-secondary text-left"
                    >
                      <span className="serif text-sm text-foreground truncate">{s.name}</span>
                      {on ? (
                        <span className="text-[hsl(var(--primary))]"><Check size={14} /></span>
                      ) : (
                        <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {s.item_count ?? s.pin_count ?? 0}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <form onSubmit={createAndAdd} className="flex items-center gap-2 mt-2 mb-3">
            <input
              data-testid="add-to-collection-new"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New collection…"
              maxLength={80}
              className="input-line flex-1 text-xs"
            />
            <button
              type="submit"
              disabled={busy || !newName.trim()}
              className="reaction-pill disabled:opacity-40"
              data-testid="add-to-collection-create"
            >
              <Plus size={11} />
            </button>
          </form>
          <button
            data-testid="add-to-collection-save"
            onClick={save}
            disabled={busy}
            className="btn-gold w-full text-xs disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}
